import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.milstudio.in';

const ChairList = () => {
  const navigate = useNavigate();
  const [chairs, setChairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [deleteModal, setDeleteModal] = useState({ show: false, chairId: null, chairName: '' });

  useEffect(() => {
    const fetchChairs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}list-chair.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setChairs(data.data);
        } else {
          setError('Failed to load chairs.');
        }
      } catch (err) {
        setError('Error fetching chairs.');
      } finally {
        setLoading(false);
      }
    };
    fetchChairs();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleEdit = (chair) => {
    navigate(`/add-chair?id=${chair.id}`, { state: { chair } });
    showToast(`Editing chair: ${chair.chairname}`, 'info');
  };

  const handleDeleteClick = (chair) => {
    setDeleteModal({ show: true, chairId: chair.id, chairName: chair.chairname });
  };

  const handleDeleteConfirm = async () => {
    const { chairId, chairName } = deleteModal;
    try {
      const response = await fetch(`${API_URL}delete-chair.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: chairId
        }),
      });

      const data = await response.json();
      
      if (data.status === true || data.message === 'Chair deleted successfully') {
        setChairs(chairs.filter(chair => chair.id !== chairId));
        showToast(`Chair "${chairName}" deleted successfully`, 'success');
      } else {
        showToast(data.message || 'Failed to delete chair', 'error');
      }
    } catch (error) {
      console.error('Error deleting chair:', error);
      showToast('Failed to delete chair. Please try again.', 'error');
    } finally {
      setDeleteModal({ show: false, chairId: null, chairName: '' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, chairId: null, chairName: '' });
  };

  const handleAddChair = () => {
    navigate('/add-chair');
    showToast('Navigating to add new chair', 'info');
  };

  return (
    <div className="chair-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Chair Management</h1>
          <p>Manage restaurant chairs</p>
        </div>
        <button className="add-unit-button" onClick={handleAddChair}>
          + Add New Chair
        </button>
      </div>

      {loading ? (
        <div>Loading chairs...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="unit-list-table-container">
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>Chair Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {chairs.map(chair => (
                <tr key={chair.id}>
                  <td>
                    <div className="unit-info">
                      <div className="unit-icon">🪑</div>
                      <span>{chair.chairname}</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(chair)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeleteClick(chair)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-footer">
        <div className="results-info">
          Showing {chairs.length} chairs
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the chair "<strong>{deleteModal.chairName}</strong>"?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleDeleteCancel}>
                No
              </button>
              <button className="btn btn-danger" onClick={handleDeleteConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChairList; 